/**
 * <product-form>
 *
 * Owns AJAX cart submission for the product form. Intercepts the add-to-cart
 * button click + form submit, validates against the inventory-derived max,
 * posts to /cart/add.js, then dispatches Dawn-style events so apps and
 * other components (the success modal, cart drawer, headers) can react.
 *
 * Logica 1:1 overgenomen uit het inline script van section-product-standard.liquid
 * (setupAddToCartHandler + handleAddToCart + toast).
 *
 * Dawn-style events emitted on `document`:
 *   - cart:add { sectionId, response, quantity, formatMoney }
 *   - cart:update { sectionId, response }         (additive; broader API)
 *
 * Required attributes:
 *   data-section : section id
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.form = null;
    this.button = null;
  }

  connectedCallback() {
    this._setupHandlers();
  }

  _setupHandlers() {
    if (!this._tryAttach()) {
      // Retry once after a tick (legacy behavior: handles late-rendered forms).
      setTimeout(() => this._tryAttach(), 500);
    }
  }

  _tryAttach() {
    let addToCartBtn = document.getElementById(`product-submit-${this.sectionId}`);
    let productForm = document.getElementById(`product-form-${this.sectionId}`);

    if (!addToCartBtn) {
      addToCartBtn = document.querySelector(`[id*="product-submit"][id*="${this.sectionId}"]`);
    }
    if (!addToCartBtn) addToCartBtn = document.querySelector('button[id*="product-submit"]');

    if (!productForm) {
      productForm = document.querySelector(`form[data-section-id="${this.sectionId}"]`);
    }
    if (!productForm && addToCartBtn) productForm = addToCartBtn.closest('form');
    if (!productForm) productForm = document.querySelector('form[action*="/cart/add"]');
    if (!addToCartBtn || !productForm) return false;

    this.form = productForm;
    this.button = addToCartBtn;

    // Listen to the form's native submit event in the bubble phase. This way
    // any third-party app handler (Subscribe & Save, ReConvert, bundle apps,
    // post-purchase upsell, …) that attaches its own submit/click listener
    // still receives the event. We only preventDefault() so the browser
    // doesn't navigate; we never call stopPropagation()/stopImmediate so the
    // app handlers keep running in their registered order.
    productForm.addEventListener('submit', (event) => {
      if (addToCartBtn.hasAttribute('disabled')) {
        event.preventDefault();
        return;
      }
      // Allow apps to cancel by calling event.preventDefault() in their own
      // submit listener (Dawn pattern).
      if (event.defaultPrevented) return;
      event.preventDefault();
      this._handleAddToCart(productForm, addToCartBtn);
    });

    return true;
  }

  _toggleLoadingState(isLoading) {
    const stickyHeader = document.getElementById(`sticky-product-header-${this.sectionId}`);
    const stickyAddBtn =
      stickyHeader?.querySelector('[data-sticky-add-to-cart]') ||
      stickyHeader?.querySelector('button[onclick*="product-submit"]');
    const buttons = [this.button];
    if (stickyAddBtn) buttons.push(stickyAddBtn);

    buttons.forEach((button) => {
      if (!button) return;
      if (isLoading) {
        button.dataset.prevDisabled = button.hasAttribute('disabled') ? 'true' : 'false';
        button.classList.add('loading');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
        if (button.dataset.prevDisabled === 'false') button.removeAttribute('disabled');
        delete button.dataset.prevDisabled;
      }
    });
  }

  _formatMoney(cents) {
    // Zie product-info.js formatMoney — standaard cents, fallback voor euro
    // strings uit /products/<handle>.json.
    if (cents == null || cents === '') return '';
    if (typeof cents === 'string') {
      const asFloat = parseFloat(cents);
      if (isNaN(asFloat)) return '';
      return '€' + asFloat.toFixed(2).replace('.', ',');
    }
    const numCents = Number(cents);
    if (isNaN(numCents)) return '';
    return '€' + (numCents / 100).toFixed(2).replace('.', ',');
  }

  _showQuantityError(message) {
    const errorEl = document.getElementById(`quantity-error-${this.sectionId}`);
    const errorMsgEl = errorEl?.querySelector('[data-quantity-error-message]');
    if (errorEl && errorMsgEl) {
      errorMsgEl.textContent = message;
      errorEl.classList.remove('hidden');
      setTimeout(() => errorEl.classList.add('hidden'), 4000);
    }
  }

  _handleAddToCart(form, button) {
    if (button.hasAttribute('disabled')) return;

    const quantityInput = document.getElementById(`quantity-${this.sectionId}`);
    const requestedQuantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
    const maxQuantity = quantityInput?.getAttribute('data-max-quantity');

    if (maxQuantity) {
      const maxQty = parseInt(maxQuantity, 10);
      if (requestedQuantity > maxQty) {
        this._showQuantityError(
          `Je kunt maximaal ${maxQty} ${maxQty === 1 ? 'stuk' : 'stukken'} toevoegen (er zijn er ${maxQty} op voorraad).`
        );
        if (quantityInput) quantityInput.value = maxQty.toString();
        this._toggleLoadingState(false);
        return;
      }
    }

    const formData = new FormData(form);
    this._toggleLoadingState(true);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw data;
        return data;
      })
      .then((data) => {
        this._toggleLoadingState(false);

        // Cart count update — kept as-is (used by header badge).
        const cartCountEls = document.querySelectorAll('[data-cart-count], .cart-count-bubble');
        if (cartCountEls.length && data?.item_count !== undefined) {
          const count = data.item_count || 0;
          const hasItems = count > 0;
          cartCountEls.forEach((el) => {
            el.textContent = hasItems ? String(count) : '';
            el.classList.toggle('hidden', !hasItems);
          });
        }

        // Notify cart drawer and any third-party app that wants to react.
        const detail = {
          sectionId: this.sectionId,
          response: data,
          quantity: requestedQuantity,
          formatMoney: this._formatMoney.bind(this),
          source: 'product-form',
          productVariantId: formData.get('id'),
        };
        document.dispatchEvent(new CustomEvent('cart:add', { detail }));
        document.dispatchEvent(new CustomEvent('cart:update', { detail }));
        // Shopify legacy/standard cart event for compatibility with older apps
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { detail, bubbles: true }));

        // Dawn 2.0+ PUB_SUB bus bridge (optional shim from shopify-compat.js).
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
        }
      })
      .catch((error) => {
        this._toggleLoadingState(false);
        const recipientActive = form.querySelector(
          'recipient-form input[type="checkbox"][name="properties[__shopify_send_gift_card_to_recipient]"]:checked'
        );

        if (recipientActive) {
          const fieldErrors = this._recipientFieldErrors(error);
          const errorMessage = this._cartErrorMessage(error);
          if (fieldErrors && typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: fieldErrors,
              message: errorMessage,
            });
            return;
          }
          this._showQuantityError(errorMessage);
          return;
        }

        const errorMessage =
          error?.message || error?.description || 'Er is een fout opgetreden bij het toevoegen aan de winkelwagen.';
        this._showQuantityError(errorMessage);
      });
  }

  _recipientFieldErrors(error) {
    const sources = [error?.errors, error?.description];
    const keys = ['email', 'name', 'message', 'send_on'];
    for (const source of sources) {
      if (!source || typeof source !== 'object' || Array.isArray(source)) continue;
      const picked = {};
      let found = false;
      keys.forEach((key) => {
        if (source[key] == null || source[key] === '') return;
        picked[key] = Array.isArray(source[key]) ? source[key] : [String(source[key])];
        found = true;
      });
      if (found) return picked;
    }

    const message = this._cartErrorMessage(error).toLowerCase();
    if (message.includes('email')) return { email: [this._cartErrorMessage(error)] };
    if (message.includes('send on') || message.includes('send_on')) return { send_on: [this._cartErrorMessage(error)] };
    if (message.includes('message') && (message.includes('long') || message.includes('maximum'))) {
      return { message: [this._cartErrorMessage(error)] };
    }
    if (message.includes('name') && (message.includes('long') || message.includes('maximum'))) {
      return { name: [this._cartErrorMessage(error)] };
    }
    return null;
  }

  _cartErrorMessage(error) {
    if (typeof error?.description === 'string' && error.description) return error.description;
    if (typeof error?.message === 'string' && error.message) return error.message;
    if (typeof error === 'string' && error) return error;
    return 'Er is een fout opgetreden bij het toevoegen aan de winkelwagen.';
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

/* ---------------------------- Toast notifier ----------------------------- */
/**
 * Lightweight, document-scoped toast: any code can call
 *   window.SpiruToast.show(message, sectionId?) or document.dispatchEvent
 *   new CustomEvent('toast:show', { detail: { message, sectionId } });
 */
window.SpiruToast = window.SpiruToast || {
  show(message, sectionId) {
    let toast = sectionId ? document.getElementById(`toast-notification-${sectionId}`) : null;
    if (!toast) toast = document.querySelector('[id^="toast-notification-"]');
    if (!toast) return;

    const messageEl = toast.querySelector('[data-toast-message]');
    if (messageEl) messageEl.textContent = message;
    toast.classList.remove('hidden');
    toast.setAttribute('aria-hidden', 'false');

    const autoHide = setTimeout(() => this.hide(toast), 5000);
    const closeBtn = toast.querySelector('[data-toast-close]');
    if (closeBtn) {
      const closeHandler = () => {
        clearTimeout(autoHide);
        this.hide(toast);
        closeBtn.removeEventListener('click', closeHandler);
      };
      closeBtn.addEventListener('click', closeHandler);
    }
  },
  hide(toast) {
    if (!toast) return;
    toast.classList.add('hidden');
    toast.setAttribute('aria-hidden', 'true');
  },
};

document.addEventListener('toast:show', (e) => {
  const detail = e.detail || {};
  window.SpiruToast.show(detail.message || '', detail.sectionId);
});
